import { toast } from "sonner";

const meta = (b) =>
  b >= 100 ? { color: "#F5C518", label: "100-Day" }
  : b >= 30 ? { color: "#C9CBD1", label: "30-Day" }
  : { color: "#CD7F32", label: "7-Day" };

// Generates a branded square PNG of the streak badge and shares it (Web Share API),
// falling back to download + caption-to-clipboard so parents can post it anywhere.
export async function shareBadgeImage(badge) {
  const m = meta(badge);
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#111111";
  ctx.fillRect(0, 0, 1080, 1080);
  ctx.strokeStyle = m.color;
  ctx.lineWidth = 16;
  ctx.strokeRect(28, 28, 1024, 1024);

  ctx.textAlign = "center";
  ctx.fillStyle = "#C41E24";
  ctx.font = "800 54px Arial";
  ctx.fillText("NEXT O SOCCER", 540, 150);
  ctx.fillStyle = "#888888";
  ctx.font = "600 26px Arial";
  ctx.fillText("ELITE TECHNICAL TRAINING SYSTEM", 540, 198);

  ctx.beginPath();
  ctx.arc(540, 500, 185, 0, Math.PI * 2);
  ctx.lineWidth = 18;
  ctx.strokeStyle = m.color;
  ctx.stroke();
  ctx.fillStyle = m.color;
  ctx.font = "800 210px Arial";
  ctx.textBaseline = "middle";
  ctx.fillText(String(badge), 540, 510);
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = "#ffffff";
  ctx.font = "800 66px Arial";
  ctx.fillText("DAY STREAK", 540, 780);
  ctx.fillStyle = "#ffffff";
  ctx.font = "700 44px Arial";
  ctx.fillText(`I just crushed a ${badge}-day streak!`, 540, 905);
  ctx.fillStyle = "#C41E24";
  ctx.font = "700 32px Arial";
  ctx.fillText("Train with me — Next O Soccer", 540, 975);

  const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
  const caption = `I just hit a ${m.label} training streak with Next O Soccer! ⚽🔥 #NextOSoccer`;
  const file = new File([blob], "next-o-streak.png", { type: "image/png" });

  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text: caption, title: "Next O Soccer Streak" });
      return;
    }
  } catch (e) {
    // user cancelled or share unsupported → fall through to download
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "next-o-streak.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  try { await navigator.clipboard.writeText(caption); } catch (e) { /* clipboard optional */ }
  toast.success("Badge image saved & caption copied — share it anywhere!");
}

// Player of the Week shareable card.
export async function sharePotwImage(name, streak) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#111111";
  ctx.fillRect(0, 0, 1080, 1080);
  ctx.strokeStyle = "#C41E24";
  ctx.lineWidth = 16;
  ctx.strokeRect(28, 28, 1024, 1024);
  ctx.textAlign = "center";
  ctx.fillStyle = "#F5C518";
  ctx.font = "800 46px Arial";
  ctx.fillText("★ PLAYER OF THE WEEK ★", 540, 150);
  ctx.fillStyle = "#C41E24";
  ctx.font = "800 44px Arial";
  ctx.fillText("NEXT O SOCCER", 540, 220);

  ctx.beginPath();
  ctx.arc(540, 480, 150, 0, Math.PI * 2);
  ctx.lineWidth = 18;
  ctx.strokeStyle = "#F5C518";
  ctx.stroke();
  ctx.fillStyle = "#F5C518";
  ctx.font = "800 120px Arial";
  ctx.textBaseline = "middle";
  ctx.fillText("🏆", 540, 490);
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = "#ffffff";
  ctx.font = "800 84px Arial";
  ctx.fillText(name, 540, 720);
  ctx.fillStyle = "#C41E24";
  ctx.font = "800 56px Arial";
  ctx.fillText(`${streak}-DAY STREAK 🔥`, 540, 810);
  ctx.fillStyle = "#888888";
  ctx.font = "700 34px Arial";
  ctx.fillText("Elite Technical Training System · Ball Mastery Vol. 1", 540, 940);

  const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
  const caption = `${name} is Next O Soccer's Player of the Week with a ${streak}-day training streak! 🏆⚽ #NextOSoccer`;
  const file = new File([blob], "next-o-player-of-week.png", { type: "image/png" });
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text: caption, title: "Next O Soccer Player of the Week" });
      return;
    }
  } catch (e) { /* share cancelled/unsupported → fall through to download */ }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "next-o-player-of-week.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  try { await navigator.clipboard.writeText(caption); } catch (e) { /* clipboard optional */ }
  toast.success("Shout-out image saved & caption copied — share it anywhere!");
}

// Weekly stats share card — points, streak, rank + movement.
export async function shareWeeklyImage({ points = 0, streak = 0, rank = null, movement = null, name = "" } = {}) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#111111";
  ctx.fillRect(0, 0, 1080, 1080);
  ctx.strokeStyle = "#C41E24";
  ctx.lineWidth = 16;
  ctx.strokeRect(28, 28, 1024, 1024);
  ctx.textAlign = "center";

  ctx.fillStyle = "#C41E24";
  ctx.font = "800 46px Arial";
  ctx.fillText("MY TRAINING WEEK", 540, 150);
  ctx.fillStyle = "#888888";
  ctx.font = "600 26px Arial";
  ctx.fillText("NEXT O SOCCER · ELITE TECHNICAL TRAINING", 540, 198);
  if (name) {
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 60px Arial";
    ctx.fillText(name.toUpperCase(), 540, 300);
  }

  // Three stat blocks
  const stats = [
    { v: String(points), l: "POINTS", c: "#C41E24" },
    { v: `${streak}🔥`, l: "DAY STREAK", c: "#F5C518" },
    { v: rank ? `#${rank}` : "—", l: "GLOBAL RANK", c: "#ffffff" },
  ];
  const boxW = 300, gap = 30, totalW = boxW * 3 + gap * 2;
  let x = (1080 - totalW) / 2;
  const y = 400, boxH = 260;
  stats.forEach((s) => {
    ctx.fillStyle = "#161616";
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(x, y, boxW, boxH, 20);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = s.c;
    ctx.font = "800 90px Arial";
    ctx.textBaseline = "middle";
    ctx.fillText(s.v, x + boxW / 2, y + boxH / 2 - 10);
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#888888";
    ctx.font = "700 24px Arial";
    ctx.fillText(s.l, x + boxW / 2, y + boxH - 34);
    x += boxW + gap;
  });

  // Movement line
  let moveText = "Holding my rank 💪";
  let moveColor = "#cccccc";
  if (typeof movement === "number" && movement > 0) { moveText = `▲ Up ${movement} spot${movement === 1 ? "" : "s"} this week!`; moveColor = "#22C55E"; }
  else if (typeof movement === "number" && movement < 0) { moveText = `▼ Down ${Math.abs(movement)} — climbing back!`; moveColor = "#C41E24"; }
  ctx.fillStyle = moveColor;
  ctx.font = "800 44px Arial";
  ctx.fillText(moveText, 540, 780);

  ctx.fillStyle = "#ffffff";
  ctx.font = "700 40px Arial";
  ctx.fillText("Chasing the top spot ⚽", 540, 900);
  ctx.fillStyle = "#C41E24";
  ctx.font = "700 32px Arial";
  ctx.fillText("Train with me — Next O Soccer", 540, 965);

  const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
  const caption = `My Next O Soccer training week: ${points} pts, ${streak}-day streak, ranked #${rank || "?"}! ⚽🔥 #NextOSoccer`;
  const file = new File([blob], "next-o-my-week.png", { type: "image/png" });
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text: caption, title: "My Next O Soccer Week" });
      return;
    }
  } catch (e) { /* share cancelled/unsupported → fall through to download */ }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "next-o-my-week.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  try { await navigator.clipboard.writeText(caption); } catch (e) { /* clipboard optional */ }
  toast.success("Your week card saved & caption copied — share it anywhere!");
}
